// src/prototype/enterprise/evolution/EvolutionView.tsx
// ============================================================
// ENTERPRISE · INITIATIVE EVOLUTION VIEW (Surface "1i") — the shared A∩C modal.
//
// Opened from the Portfolio grid's "evolution →" and the Decision log's "View initiative
// evolution →". Renders the trajectory chart (composite + confidence across the 3 runs) with
// DECISION MARKERS overlaid on the timeline, current dimension bars, and the decision history.
// Read-only via buildEvolution (spine adapters). Layout mirrors Enterprise Prototype.dc.html.
// ============================================================

import React from 'react';
import { STATUS_BADGE } from '../portfolio/portfolioDisplay';
import { DECISION_BADGE } from '../decisions/decisionsModel';
import { buildEvolution, EVO_X, type EvoMarker } from './evolutionModel';

export const EvolutionView: React.FC<{ useCaseId: number; onClose: () => void }> = ({ useCaseId, onClose }) => {
  const evo = buildEvolution(useCaseId);

  // Group decision markers by run x so labels at the same run stack instead of overlapping.
  const markerGroups = new Map<number, EvoMarker[]>();
  for (const m of evo.markers) {
    const g = markerGroups.get(m.x) ?? [];
    g.push(m);
    markerGroups.set(m.x, g);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f1a2c]/55 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-[960px] max-w-full max-h-[90vh] overflow-auto bg-white rounded-[14px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#e5e7eb] flex justify-between items-start gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182]">
              Initiative evolution · #{evo.useCaseId}
            </div>
            <div className="text-base font-bold text-[#18365E] mt-0.5">{evo.title}</div>
            <div className="text-[11px] text-[#717182] mt-0.5">
              Owner: {evo.owner.name} · {evo.owner.org} · {evo.category}
            </div>
          </div>
          <div className="flex gap-3.5 items-start shrink-0">
            <div className="text-center">
              <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#717182]">Rank</div>
              <div className="text-[17px] font-bold text-[#18365E]">#{evo.currentRank}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#717182]">Composite</div>
              <div className="text-[17px] font-bold text-[#18365E]">{evo.currentComp.toFixed(1)}</div>
              <div className="text-[9px]" style={{ color: evo.trendColor }}>{evo.trendLabel}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#717182]">Confidence</div>
              <div className="text-[17px] font-bold text-[#18365E]">{evo.currentConf}%</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#717182]">Status</div>
              <span className={`inline-block mt-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[evo.currentStatus]}`}>
                {evo.currentStatus}
              </span>
            </div>
            <button onClick={onClose} className="text-[#9ca3af] text-lg leading-none ml-1.5 hover:text-[#374151]" aria-label="Close">✕</button>
          </div>
        </div>

        {!evo.found ? (
          <div className="px-6 py-16 text-center text-[12px] text-[#9ca3af]">No trajectory data for this initiative.</div>
        ) : (
          <>
            {/* Trajectory chart */}
            <div className="px-6 pt-4 pb-1">
              <svg viewBox="0 0 912 170" className="block w-full" style={{ height: 'auto' }}>
                <line x1="56" y1="135" x2="880" y2="135" stroke="#e5e7eb" strokeWidth="1" />
                <line x1="56" y1="87" x2="880" y2="87" stroke="#f1f3f6" strokeWidth="1" />
                <line x1="56" y1="39" x2="880" y2="39" stroke="#f1f3f6" strokeWidth="1" />

                {/* Decision markers overlaid on the run timeline */}
                {[...markerGroups.entries()].map(([x, items]) => (
                  <g key={x}>
                    <line x1={x} y1="18" x2={x} y2="150" stroke={items[0].color} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                    {items.map((m, i) => (
                      <text key={i} x={x} y={13 + i * 12} fontSize="9" fontWeight="700" fill={m.color} textAnchor="middle">
                        {m.label}
                      </text>
                    ))}
                  </g>
                ))}

                <polyline points={evo.confPts} fill="none" stroke="#FF6E4C" strokeWidth="2" strokeDasharray="5 4" />
                <polyline points={evo.compPts} fill="none" stroke="#18365E" strokeWidth="2.5" />
                {evo.compDots.map((d, i) => (
                  <g key={i}>
                    <circle cx={d.x} cy={d.y} r="5" fill="#18365E" />
                    <text x={d.x} y={d.y - 12} fontSize="11" fontWeight="700" fill="#18365E" textAnchor="middle">{d.val.toFixed(1)}</text>
                  </g>
                ))}
              </svg>
              <div className="flex px-10 mt-0.5">
                {evo.runs.map((r, i) => (
                  <div key={i} className={`flex-1 text-[10.5px] text-[#717182] ${i === 1 ? 'text-center' : i === 2 ? 'text-right' : ''}`}>
                    <b className="text-[#18365E]">{r.label}</b><br />
                    {r.dateLabel} · rank #{r.rank} · {r.conf}% conf
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-2 text-[10px] text-[#717182] justify-end">
                <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-[#18365E]" /> Composite</span>
                <span className="flex items-center gap-1"><span className="inline-block w-4 h-0 border-t-2 border-dashed border-[#FF6E4C]" /> Confidence</span>
              </div>
            </div>

            {/* Dimension bars + decision history */}
            <div className="flex flex-col md:flex-row gap-5 px-6 pt-3.5 pb-5 border-t border-[#f1f3f6] mt-3">
              <div className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182] mb-2">
                  Dimension scores · {evo.currentRunLabel}
                </div>
                {evo.dims.map((dm) => (
                  <div key={dm.name} className="flex items-center gap-2.5 mb-[7px]">
                    <span className="w-[168px] text-[10.5px] text-[#374151]">{dm.name}</span>
                    <div className="flex-1 h-1.5 bg-[#f1f3f6] rounded-full relative">
                      <div className="absolute left-0 top-0 h-1.5 rounded-full" style={{ width: `${dm.val}%`, background: dm.accent ? '#FF6E4C' : '#18365E' }} />
                    </div>
                    <span className="w-[26px] text-[11px] font-bold text-[#18365E] text-right">{dm.val}</span>
                  </div>
                ))}
              </div>

              <div className="md:w-[320px] flex-none bg-[#F5F7FA] rounded-lg px-3.5 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#717182] mb-2">Decision history</div>
                {evo.decisions.length === 0 ? (
                  <div className="text-[10.5px] text-[#9ca3af]">No governance decisions recorded for this initiative yet.</div>
                ) : (
                  evo.decisions.map((ed, i) => (
                    <div key={i} className="mb-2.5 last:mb-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${DECISION_BADGE[ed.decision]}`}>{ed.decision}</span>
                        <span className="text-[10px] text-[#717182]">{ed.date} · {ed.by}</span>
                      </div>
                      <div className="text-[10px] text-[#374151] leading-snug mt-1">&ldquo;{ed.rationale}&rdquo;</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="px-6 py-2.5 bg-[#F5F7FA] text-[9.5px] text-[#9ca3af] rounded-b-[14px]">
              Reachable from the portfolio grid (Surface A) and the decision record (Surface C) · composite + confidence per run, decision markers on the timeline
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EvolutionView;
